/**
 * 📧 Gestión de configuración de emails por empresa
 * Permite configurar servidores SMTP diferentes para cada empresa
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Send as SendIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const GestionEmailEmpresas = () => {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [dialogPrueba, setDialogPrueba] = useState(null); // config que se está probando
  const [destinatarioPrueba, setDestinatarioPrueba] = useState('');
  const [probando, setProbando] = useState(false);
  const [resultadoPrueba, setResultadoPrueba] = useState(null); // { success, mensaje/error }

  const [formData, setFormData] = useState({
    id: null,
    empresa: '',
    email_host: 'smtp.gmail.com',
    email_port: 587,
    email_secure: false,
    email_user: '',
    email_password: '',
    email_from_name: 'Sistema RRHH',
    activo: true
  });

  useEffect(() => {
    cargarConfiguraciones();
  }, []);

  const cargarConfiguraciones = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/empresas-email`, {
        withCredentials: true
      });
      setConfiguraciones(response.data);
    } catch (error) {
      console.error('Error cargando configuraciones:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar configuraciones',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNuevo = () => {
    setFormData({
      id: null,
      empresa: '',
      email_host: 'smtp.gmail.com',
      email_port: 587,
      email_secure: false,
      email_user: '',
      email_password: '',
      email_from_name: 'Sistema RRHH',
      activo: true
    });
    setEditando(null);
    setOpenDialog(true);
  };

  const handleEditar = (config) => {
    setFormData({
      id: config.id,
      empresa: config.empresa,
      email_host: config.email_host,
      email_port: config.email_port,
      email_secure: config.email_secure === 1,
      email_user: config.email_user,
      email_password: '', // No mostrar password existente
      email_from_name: config.email_from_name,
      activo: config.activo === 1
    });
    setEditando(config);
    setOpenDialog(true);
  };

  const handleEliminar = async (id, empresa) => {
    if (!window.confirm(`¿Estás seguro de eliminar la configuración de ${empresa}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/empresas-email/${id}`, {
        withCredentials: true
      });
      setSnackbar({
        open: true,
        message: 'Configuración eliminada correctamente',
        severity: 'success'
      });
      cargarConfiguraciones();
    } catch (error) {
      console.error('Error eliminando:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error al eliminar',
        severity: 'error'
      });
    }
  };

  const handleAbrirPrueba = (config) => {
    setDialogPrueba(config);
    setDestinatarioPrueba('');
    setResultadoPrueba(null);
  };

  const handleProbar = async () => {
    if (!dialogPrueba) return;
    setProbando(true);
    setResultadoPrueba(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/empresas-email/${dialogPrueba.id}/probar`,
        destinatarioPrueba ? { destinatario: destinatarioPrueba } : {},
        { withCredentials: true }
      );
      setResultadoPrueba({ success: true, texto: response.data.mensaje });
    } catch (error) {
      setResultadoPrueba({
        success: false,
        texto: error.response?.data?.error || 'Error al probar la configuración'
      });
    } finally {
      setProbando(false);
    }
  };

  const handleGuardar = async () => {
    // Validaciones
    if (!formData.empresa || !formData.email_host || !formData.email_user || !formData.email_from_name) {
      setSnackbar({
        open: true,
        message: 'Por favor completa todos los campos obligatorios',
        severity: 'warning'
      });
      return;
    }

    if (!editando && !formData.email_password) {
      setSnackbar({
        open: true,
        message: 'La contraseña es obligatoria al crear una configuración',
        severity: 'warning'
      });
      return;
    }

    try {
      const url = formData.id
        ? `${API_BASE_URL}/api/empresas-email/${formData.id}`
        : `${API_BASE_URL}/api/empresas-email`;

      const method = formData.id ? 'put' : 'post';

      await axios[method](url, {
        ...formData,
        activo: formData.activo ? 1 : 0,
        email_secure: formData.email_secure ? 1 : 0
      }, { withCredentials: true });

      setSnackbar({
        open: true,
        message: formData.id ? 'Configuración actualizada' : 'Configuración creada',
        severity: 'success'
      });

      setOpenDialog(false);
      cargarConfiguraciones();
    } catch (error) {
      console.error('Error guardando:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error al guardar',
        severity: 'error'
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" component="h1">
              Configuración de Emails por Empresa
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNuevo}
          >
            Nueva Configuración
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Cada empresa puede tener su propio servidor SMTP. Los emails se enviarán desde el correo
            configurado según la empresa del usuario.
          </Typography>
        </Alert>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Servidor SMTP</strong></TableCell>
                <TableCell><strong>Usuario</strong></TableCell>
                <TableCell><strong>Nombre Remitente</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : configuraciones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay configuraciones. Crea una nueva.
                  </TableCell>
                </TableRow>
              ) : (
                configuraciones.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <strong>{config.empresa}</strong>
                    </TableCell>
                    <TableCell>
                      {config.email_host}:{config.email_port}
                      {config.email_secure === 1 && <Chip label="SSL" size="small" color="success" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{config.email_user}</TableCell>
                    <TableCell>{config.email_from_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.activo === 1 ? 'Activo' : 'Inactivo'}
                        color={config.activo === 1 ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="success"
                        onClick={() => handleAbrirPrueba(config)}
                        size="small"
                        title="Probar esta configuración"
                      >
                        <SendIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditar(config)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleEliminar(config.id, config.empresa)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog de crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editando ? `Editar Configuración: ${editando.empresa}` : 'Nueva Configuración de Email'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nombre de la Empresa"
              value={formData.empresa}
              onChange={(e) => handleChange('empresa', e.target.value)}
              fullWidth
              required
              helperText="Debe coincidir exactamente con el nombre en la tabla legajos"
              disabled={editando !== null}
            />

            <TextField
              label="Servidor SMTP"
              value={formData.email_host}
              onChange={(e) => handleChange('email_host', e.target.value)}
              fullWidth
              required
              placeholder="smtp.gmail.com"
            />

            <TextField
              label="Puerto"
              type="number"
              value={formData.email_port}
              onChange={(e) => handleChange('email_port', parseInt(e.target.value))}
              fullWidth
              required
              helperText="587 para TLS, 465 para SSL"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.email_secure}
                  onChange={(e) => handleChange('email_secure', e.target.checked)}
                />
              }
              label="Usar SSL (puerto 465)"
            />

            <TextField
              label="Usuario / Email"
              value={formData.email_user}
              onChange={(e) => handleChange('email_user', e.target.value)}
              fullWidth
              required
              placeholder="admin@empresa.com"
            />

            <TextField
              label="Contraseña"
              type={mostrarPassword ? 'text' : 'password'}
              value={formData.email_password}
              onChange={(e) => handleChange('email_password', e.target.value)}
              fullWidth
              required={!editando}
              helperText={editando ? 'Dejar en blanco para no cambiar' : 'Contraseña de aplicación del correo'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarPassword(!mostrarPassword)}
                      edge="end"
                    >
                      {mostrarPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              label="Nombre del Remitente"
              value={formData.email_from_name}
              onChange={(e) => handleChange('email_from_name', e.target.value)}
              fullWidth
              required
              placeholder="Sistema RRHH Empresa"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => handleChange('activo', e.target.checked)}
                />
              }
              label="Configuración activa"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            startIcon={<CancelIcon />}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de probar configuración */}
      <Dialog open={!!dialogPrueba} onClose={() => setDialogPrueba(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Probar configuración: {dialogPrueba?.empresa}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sin completar el destinatario, solo se verifica que el sistema pueda
            conectarse y autenticarse contra el servidor SMTP (host, puerto y
            credenciales). Si además ponés un email, se manda un mail de prueba real.
          </Typography>

          <TextField
            label="Email de destino (opcional)"
            value={destinatarioPrueba}
            onChange={(e) => setDestinatarioPrueba(e.target.value)}
            fullWidth
            placeholder="tu-mail@gmail.com"
            sx={{ mb: 2 }}
          />

          {resultadoPrueba && (
            <Alert severity={resultadoPrueba.success ? 'success' : 'error'}>
              {resultadoPrueba.texto}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogPrueba(null)} disabled={probando}>
            Cerrar
          </Button>
          <Button
            onClick={handleProbar}
            variant="contained"
            disabled={probando}
            startIcon={probando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {probando ? 'Probando...' : destinatarioPrueba ? 'Enviar mail de prueba' : 'Probar conexión'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GestionEmailEmpresas;
